import { ErpatClient } from '@erpat/sdk';
import type { InjectionKey } from 'vue';
import { API_BASE_URL } from '@/utils/env';
import { detectEdgeChallenge, securityChallengeError } from '@/utils/edge-challenge';

/**
 * Application-wide ERPat SDK client (`@erpat/sdk`).
 *
 * The SDK targets the first-party end-user API under `/v1/api/*`. Tenancy is
 * resolved from the `X-Company-Key` header (the company slug), which the SDK
 * attaches to every request once `companyKey` is set — including `auth.login`,
 * `auth.mfa`, and `auth.refresh`, where the server needs it to switch tenant
 * database *before* authentication runs.
 *
 * Token persistence is delegated to the SDK's `storage` adapter (here
 * `localStorage`). On construction the SDK auto-restores any persisted access /
 * refresh token, so the client is ready to make authenticated calls after a
 * page reload. `auth.login(..., { rememberLogin: true })` writes the issued
 * pair; `auth.logout()` / `clearTokens()` erase it.
 *
 * The company key is persisted separately (the SDK does not own it) so a
 * restored session can refresh its token against the right tenant.
 *
 * Usage (services / stores):  import { erpatClient } from '@/services/api/erpat-client';
 * Usage (components, typed):   const erpat = inject(erpatClientKey)!;
 */

const COMPANY_KEY_STORAGE = 'syntry_company_key';

function readStoredCompanyKey(): string | undefined {
  try {
    return localStorage.getItem(COMPANY_KEY_STORAGE) ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Per-request ceiling handed to the SDK. Its own default is this same 30 s, but
 * an implicit default cannot be compared against — and the sign-in path has to
 * compare against it. The SDK collapses "our own abort fired" and "the device has
 * no network" into an identical `ErpatApiError('network_error', …, 0)` (it
 * discards even the AbortError's name), so this number is the ONLY thing that
 * tells a slow server apart from a missing one. See `describeAuthError()` in
 * http.ts. Passing it explicitly changes no behaviour for any existing caller.
 */
export const REQUEST_TIMEOUT_MS = 30_000;

/**
 * What a request cancelled by a sign-out rejects with, as its `message`. Worded
 * for a person because it can reach one: a form the user submitted just before
 * signing out renders it through `errorMessage()`. "May not have gone through"
 * because a cancelled POST may still have been processed — cancelling stops the
 * WAIT, not work the server already started.
 */
export const SESSION_CANCELLED_MESSAGE =
  'You signed out before this finished, so it may not have gone through.';

/**
 * The error a sign-out-cancelled request rejects with. An `AbortError` on
 * purpose, so every existing "was this aborted?" check already treats it as a
 * cancellation rather than a failure; the fixed message is what tells it apart
 * from a page's own abort (see `isSessionCancelled()` in http.ts).
 */
export function sessionCancelledError(): DOMException {
  return new DOMException(SESSION_CANCELLED_MESSAGE, 'AbortError');
}

const sessionCancellers = new Set<() => void>();

/**
 * Register work that must stop when the session ends, and get back the function
 * that unregisters it. Call that once the work settles — until then the canceller,
 * and whatever it closes over, is held until the next sign-out.
 */
export function onSessionCancel(cancel: () => void): () => void {
  sessionCancellers.add(cancel);
  return () => {
    sessionCancellers.delete(cancel);
  };
}

/**
 * Cancel every request the current session still has in flight — a page load, an
 * upload, a message send, a token refresh, an assistant reply.
 *
 * Sign-out's first move, so that nothing the previous session started can hold
 * it up: a browser allows only a handful of connections per origin, and the
 * logout request would otherwise queue behind them. It also stops their answers
 * from landing in state after the local clear. Requests started AFTER this call
 * belong to whatever comes next and are untouched, which is what lets sign-out
 * still send its own logout request.
 */
export function cancelSessionRequests(): void {
  const pending = [...sessionCancellers];
  sessionCancellers.clear();
  for (const cancel of pending) {
    try {
      cancel();
    } catch {
      /* one canceller failing must not keep the rest running */
    }
  }
}

/**
 * `fetch`, as one of the session's requests: cancelled by
 * {@link cancelSessionRequests} as well as by the caller's own signal. The SDK
 * client below sends every call through it; the raw multipart uploads in
 * `services/erpat.ts` use it directly.
 *
 * Tracked only until the response HEADERS arrive — the same boundary as the SDK's
 * own timeout. That covers what holds a sign-out up (a request still waiting on a
 * busy server or on a free connection); a body that then stalls is not cut, but
 * sign-out never waits on one either, and the session epoch keeps whatever it
 * eventually returns out of the next session. A caller that streams a long body
 * (the assistant) registers its own canceller for the whole read.
 *
 * The caller's signal is linked for the life of the request, so an abort during
 * the body read still reaches it — which assumes a per-request signal. The SDK
 * always hands one (it builds a fresh controller per call).
 *
 * This is also the ONLY place the raw `Response` is visible. The SDK discards
 * the headers, and for a rejected fetch it — like the multipart uploaders —
 * keeps nothing but the error's `message`. So an edge's security challenge
 * (a web page answered INSTEAD of ERPat, marked `cf-mitigated: challenge`) has
 * to be recognised here, and is turned into a rejection with a fixed message.
 * Downstream that reads as status 0, which is the literal truth — ERPat never
 * saw the request — and is told apart from a lost connection by
 * `isSecurityChallenge()` (utils/edge-challenge.ts). Left alone, the page's 403
 * would be read as an answer: "not available for your account", "wrong
 * password", "the refresh token was rejected — sign out".
 *
 * It also keeps the browser's HTTP cache out of every request (`cache:
 * 'no-store'` unless the caller sets a mode): an API answer must come from the
 * server, never from a copy the browser kept. A backend that once served a raw
 * file in an API path's place (PHP source, a web page) sends `Last-Modified`,
 * which Chrome treats as fresh for a tenth of the file's age — weeks — and
 * replays without asking, whichever backend the dev proxy points at by then.
 * That is how the sign-in ping read "Can't reach the system" against a healthy
 * server. Nothing is lost: ERPat sends no freshness headers, offline reads are
 * the service worker's job (Cache Storage ignores the mode), and the
 * `Cache-Control` / `Pragma: no-cache` the browser adds for it are not CORS
 * request headers, so no new preflight.
 */
export async function sessionFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const controller = new AbortController();
  const callerSignal = init.signal ?? undefined;
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort(callerSignal.reason);
    else callerSignal.addEventListener('abort', () => controller.abort(callerSignal.reason), { once: true });
  }

  let cancelled = false;
  const untrack = onSessionCancel(() => {
    cancelled = true;
    controller.abort(sessionCancelledError());
  });

  try {
    // Resolved at call time, not captured at import, so a stubbed or patched
    // global `fetch` is honoured. `no-store` unless the caller chose a mode — a
    // stale copy in the HTTP cache must never stand in for the server (above).
    const response = await globalThis.fetch(input, {
      ...init,
      cache: init.cache ?? 'no-store',
      signal: controller.signal,
    });
    // Not an answer from ERPat — reject before anything can read its status as
    // one. The body is a web page nobody will read, so release the connection.
    if (detectEdgeChallenge(response.status, (name) => response.headers.get(name))) {
      void response.body?.cancel().catch(() => {});
      throw securityChallengeError();
    }
    return response;
  } catch (err) {
    // Rethrown as OUR error whatever the engine rejected with: older engines
    // ignore an abort reason, and this message is how a cancellation is told
    // apart from a network failure once the SDK has wrapped it.
    if (cancelled) throw sessionCancelledError();
    throw err;
  } finally {
    untrack();
  }
}

export const erpatClient = new ErpatClient({
  baseUrl: API_BASE_URL,
  companyKey: readStoredCompanyKey(),
  timeoutMs: REQUEST_TIMEOUT_MS,
  // Every SDK request can be cancelled by a sign-out (see sessionFetch).
  fetch: sessionFetch,
  // Auto-restores tokens on construction and keeps them in sync on refresh.
  storage: localStorage,
});

/** The tenant slug the client is currently scoped to, if any. */
export function getStoredCompanyKey(): string | undefined {
  return readStoredCompanyKey();
}

/**
 * Set the active tenant and persist it. Call before `auth.login()` so the
 * `X-Company-Key` header is present, and so a future token refresh (after a
 * reload) can resolve the same tenant.
 */
export function rememberCompanyKey(slug: string): void {
  erpatClient.setCompanyKey(slug);
  try {
    localStorage.setItem(COMPANY_KEY_STORAGE, slug);
  } catch {
    /* private mode / storage disabled — header still set in-memory */
  }
}

/**
 * The SDK's persisted-token storage keys. `@erpat/sdk` does not export them
 * (see `STORAGE_ACCESS_KEY` / `STORAGE_REFRESH_KEY` in its `client.ts`), so
 * they are pinned here for the cross-tab token sync below. The server rotates
 * the refresh token on every refresh and revokes the WHOLE session family when
 * a consumed token is replayed — so a tab holding a stale in-memory pair must
 * adopt what another tab persisted instead of presenting the old token.
 */
export const SDK_ACCESS_TOKEN_KEY = 'erpat.access_token';
export const SDK_REFRESH_TOKEN_KEY = 'erpat.refresh_token';

/** True when the cached access token expires within `seconds` (or can't be read). */
export function accessTokenExpiringWithin(seconds: number): boolean {
  try {
    const { exp } = erpatClient.auth.decode();
    if (typeof exp !== 'number') return true;
    return exp - Date.now() / 1000 <= seconds;
  } catch {
    // No/garbled access token — treat as expiring so callers refresh.
    return true;
  }
}

/**
 * The signed-in user's id, read from the access token's `sub` claim, or '' when
 * there is no readable token.
 *
 * Deliberately taken from the token and not `useUserStore().id`: the store is
 * populated by an async profile fetch, so it is '' for the first moments of a
 * session. Anything that KEYS storage by user needs an answer that is correct
 * from the instant a token exists, or early writes land under one key and later
 * reads look under another.
 */
export function getSessionUserId(): string {
  try {
    const { sub } = erpatClient.auth.decode();
    return typeof sub === 'string' ? sub.trim() : '';
  } catch {
    // No/garbled access token — no identity to scope by.
    return '';
  }
}

/**
 * Load the token pair persisted in `localStorage` into the client when it
 * differs from the in-memory pair — i.e. another tab rotated the tokens after
 * this tab constructed its client (the SDK only reads storage on construction).
 *
 * @returns `true` when a differing pair was found and adopted.
 */
export function adoptPersistedTokens(): boolean {
  try {
    const access = localStorage.getItem(SDK_ACCESS_TOKEN_KEY);
    const refresh = localStorage.getItem(SDK_REFRESH_TOKEN_KEY);
    if (!access || !refresh) return false;
    if (access === erpatClient.getAccessToken() && refresh === erpatClient.getRefreshToken()) {
      return false;
    }
    erpatClient.setTokens({ accessToken: access, refreshToken: refresh });
    return true;
  } catch {
    return false;
  }
}

let sessionEpoch = 0;

/**
 * Monotonic session generation, bumped on every teardown (see {@link clearSession}).
 * An async operation (e.g. an in-flight token refresh) can capture this before
 * its `await` and re-check it afterward to detect that a sign-out / expiry
 * happened meanwhile — and so discard its now-stale result instead of acting on
 * it. Without this, a refresh that resolves *after* sign-out would let the SDK
 * silently repopulate the in-memory tokens and resurrect the session.
 */
export function getSessionEpoch(): number {
  return sessionEpoch;
}

/** Clear cached tokens (and persisted copies). Keeps the remembered company key. */
export function clearSession(): void {
  sessionEpoch++;
  erpatClient.clearTokens();
}

/** True when an access token is cached (restored from storage or just issued). */
export function hasAccessToken(): boolean {
  return Boolean(erpatClient.getAccessToken());
}

/** Vue injection key — provided in main.ts, injectable in any component. */
export const erpatClientKey: InjectionKey<ErpatClient> = Symbol('erpatClient');
